import React, { useCallback, useEffect, useRef, useState } from "react";
import { getRoleOrders, sortRoles } from "@/utils/roleUtils";
import {
    escapeHtml,
    extractRoleNames,
    getShiftColor,
    getShiftDisplayContent,
    getShiftTooltipData,
    getTooltipPosition,
    isShiftVisibleForFilter,
    type TooltipState
} from "@/utils/schedulerUtils";
import { CalendarDays, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Minus, Plus } from "lucide-react";
import type { TimelineOptions } from "vis-timeline/standalone";
import { DataSet, Timeline } from "vis-timeline/standalone";
import "vis-timeline/styles/vis-timeline-graph2d.min.css";
import type { Employee, Schedule, Shift } from "./types";
import ShiftBreachTooltip from "@/components/tooltips/ShiftBreachTooltip";
import type { SchedulerAppContext, TimelineData } from "@/SchedulerApp.types.ts";
import RoleCalendarModal from "@/components/modals/RoleCalendarModal.tsx";
import EmployeeCalendarModal from "@/components/modals/EmployeeCalendarModal.tsx";
import GlobalCalendarModal, { DEFAULT_CALENDAR_OTHERS } from "@/components/modals/GlobalCalendarModal.tsx";
import TimelineFilterOverlay from "@/components/overlays/TimelineFilterOverlay";
import FilterButton from "@/components/button/FilterButton";


type ExtentedTimeline = Timeline & { dom: { root: HTMLElement } }

const ZOOM_MIN = 2 * 24 * 60 * 60 * 1000; // 2 jours
const ZOOM_MAX = 4 * 7 * 24 * 60 * 60 * 1000; // 4 semaines
const INITIAL_WINDOW_DAYS = 7;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_CALENDAR_OTHERS_SET = new Set(DEFAULT_CALENDAR_OTHERS);

const DAY_SCALE_THRESHOLD_MS = 24 * 24 * 60 * 60 * 1000;
const mapOfDayOfWeekLetters: Record<number, string> = {
    0: "D",
    1: "L",
    2: "M",
    3: "M",
    4: "J",
    5: "V",
    6: "S"
}
const pad2 = (n: number) => String(n).padStart(2, "0");
const formatHour = (d: Date) => `${pad2(d.getHours())}:00`;
const formatDayMonth = (d: Date) => `${mapOfDayOfWeekLetters[d.getDay()]} ${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;

const formatFullDate = (d: Date): string => {
    try {
        return new Intl.DateTimeFormat("fr-FR", { weekday: "short", day: "2-digit", month: "short" })
            .format(d)
            .replaceAll(".", "");
    } catch (e) {
        console.error(e);
        return d.toDateString();
    }
};

const formatMonthYear = (d: Date): string => {
    try {
        return new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(d);
    } catch (e) {
        console.error(e);
        return d.toDateString();
    }
};

const TIMELINE_FORMAT = {
    minorLabels: (date: { valueOf(): number }, scale: string) => {
        const d = new Date(date.valueOf());
        return scale === "hour" ? formatHour(d) : formatDayMonth(d);
    },
    majorLabels: (date: { valueOf(): number }, scale: string) => {
        const d = new Date(date.valueOf());
        return scale === "hour" ? formatFullDate(d) : formatMonthYear(d);
    },
};

const HOUR_AXIS: TimelineOptions = { timeAxis: { scale: "hour", step: 8 }, format: TIMELINE_FORMAT };
const DAY_AXIS: TimelineOptions = { timeAxis: { scale: "day", step: 1 }, format: TIMELINE_FORMAT };
const MIN_MS_PER_PX_CHANGE_AXIS = 1000 * 60 * 6 * 2;
//const MIN_MS_PER_PX_ZOOM_LEVEL = 1000*60*6*2;

// const fixZoomLevel = function(tl: Timeline, modeRef: { current: "hour" | "day" | null }){
//     const timelineWidth = (tl as ExtentedTimeline ).dom.root.offsetWidth; // px

//     const { start, end } = tl.getWindow();
//     const windowInMillis = +end - +start;
//     const msPerPixel = timelineWidth !== 0 ?  windowInMillis / timelineWidth : 0;
//     const maxMsPerPixel = MIN_MS_PER_PX_ZOOM_LEVEL

//     //const mode: "hour" | "day" = windowInMillis >= DAY_SCALE_THRESHOLD_MS ? "day" : "hour";
//     const mode: "hour" | "day" = msPerPixel > maxMsPerPixel ? "day" : "hour";

//     if(msPerPixel > maxMsPerPixel){}

// }
const applyAxisForWindow = (tl: Timeline, modeRef: { current: "hour" | "day" | null }) => {
    const timelineDom = (tl as ExtentedTimeline).dom.root
    const timelineWidth = timelineDom.offsetWidth; // px
    const { start, end } = tl.getWindow();
    const windowInMillis = +end - +start;
    const msPerPixel = timelineWidth !== 0 ? windowInMillis / timelineWidth : 0;
    const maxMsPerPixel = MIN_MS_PER_PX_CHANGE_AXIS

    //const mode: "hour" | "day" = windowInMillis >= DAY_SCALE_THRESHOLD_MS ? "day" : "hour";
    const mode: "hour" | "day" = msPerPixel > maxMsPerPixel ? "day" : "hour";
    if (modeRef.current === mode) {
        return;
    }

    timelineDom.classList.toggle('zoomed-out', mode === "day");

    modeRef.current = mode;
    tl.setOptions(mode === "day" ? DAY_AXIS : HOUR_AXIS);
};

type TimeSlot = {
    start: string;
    end: string;
};

type TimelineEvent = {
    item?: string | number;
    group?: string | number;
    what?: string;
    event?: {
        srcEvent?: MouseEvent;
        clientX?: number;
        clientY?: number;
        pageX?: number;
        pageY?: number;
    };
};

const createPlaceholderContent = (message: string): string => {
    return `<div class="text-gray-900 text-sm font-semibold p-1">${escapeHtml(message)}</div>`;
};

const buildEmployeeGroupContent = (employee: any): string => {
    const roles = extractRoleNames(employee);
    const nameHtml = `<div class="text-sm font-semibold text-gray-900 cursor-pointer hover:underline">${escapeHtml(employee?.name ?? "")}</div>`;

    if (roles.length === 0) {
        return `<div class="p-1">${nameHtml}</div>`;
    }

    const tags = roles
        .map((role) =>
            `<span class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-blue-600 text-white mr-1 mt-1">${escapeHtml(role)}</span>`
        )
        .join("");

    //return `<div class="p-1 max-w-56">${nameHtml}<div class="mt-1 flex flex-wrap">${tags}</div></div>`;
    return `<div class="p-1 max-w-56">${nameHtml}</div>`;
};

const addEmployeeDateBackgrounds = (
    employee: Employee,
    items: DataSet<any>,
    dates: TimeSlot[] | undefined,
    type: 'unavailable' | 'undesired' | 'desired'
) => {
    const configs = {
        unavailable: { content: 'Absent', color: '#ef2929' },
        undesired: { content: 'Indispo.', color: '#f57900' },
        desired: { content: 'Désiré', color: '#73d216' },
    };
    const config = configs[type];

    for (const [idx, rawDate] of (dates || []).entries()) {
        const start = rawDate.start
        const end = rawDate.end
        items.add({
            id: `${employee.userId}-${type}-${idx}`,
            group: employee.userId,
            content: config.content,
            start,
            end,
            type: 'background',
            style: `opacity: 0.5; background-color: ${config.color}`,
        });
    }
};

const DAY_MS = 86_400_000;

type Holiday = { iso: string; start: number };
const holidays: Holiday[] = [];

const setHolidays = (input: string[] | undefined) => {
    holidays.length = 0;
    for (const iso of input || []) {
        holidays.push({ iso, start: +new Date(`${iso}T00:00:00`) });
    }
};

const addHolidayBackgrounds = (input: string[] | undefined, items: DataSet<any>) => {
    setHolidays(input);

    for (const { iso, start } of holidays) {
        items.add({
            id: `holiday-${iso}`,
            content: '',
            start,
            end: start + DAY_MS,
            type: 'background',
            style: 'background-color: var(--holiday-bg)',
        });
    }
};

const fillHolidayHeader = (anchor: HTMLElement, tl: Timeline) => {
    const header = anchor.querySelector('.vis-panel.vis-top') as HTMLElement | null;
    if (!header) {
        return;
    }

    header.querySelectorAll('.vis-holiday-overlay').forEach((el) => el.remove());

    if (holidays.length === 0) {
        return;
    }

    const window = tl.getWindow();
    const winStart = +new Date(window.start);
    const winEnd = +new Date(window.end);
    const span = winEnd - winStart;
    const width = header.offsetWidth;

    if (span <= 0 || width <= 0) {
        return;
    }

    for (const { start } of holidays) {
        const visibleStart = Math.max(start, winStart);
        const visibleEnd = Math.min(start + DAY_MS, winEnd);

        if (visibleEnd <= visibleStart) {
            continue;
        }

        const leftPx = (visibleStart - winStart) / span * width;
        const widthPx = (visibleEnd - visibleStart) / span * width;

        const overlay = document.createElement('div');
        overlay.className = 'vis-holiday-overlay';
        overlay.style.cssText = `position:absolute;top:0;bottom:0;left:${leftPx}px;width:${widthPx}px;background-color:var(--holiday-bg);pointer-events:none;z-index:0;`;
        header.insertBefore(overlay, header.firstChild);
    }
};

const buildShiftBorder = (shift: Shift, problematicShiftIds?: string[]) =>
    (problematicShiftIds?.includes(shift.id) ? 'border: 2px solid #fc0505;' : '');

const buildEmployeeShiftItem = (shift: Shift, problematicShiftIds?: string[]) => ({
    id: shift.id,
    group: shift.employee!.userId,
    content: escapeHtml(shift.roleName || shift.requiredSkill || ''),
    start: shift.start,
    end: shift.end,
    style: `background-color: ${getShiftColor(shift, shift.employee!)}; ${buildShiftBorder(shift, problematicShiftIds)}border-radius:8px;`,
});

const buildLocationShiftItem = (shift: Shift, problematicShiftIds?: string[]) => ({
    id: shift.id,
    group: shift.roleName || shift.requiredSkill || 'Autre',
    content: escapeHtml(getShiftDisplayContent(shift)),
    start: shift.start,
    end: shift.end,
    style: `background-color: ${getShiftColor(shift, shift.employee || undefined)}; ${buildShiftBorder(shift, problematicShiftIds)}border-radius:8px;`,
});

const renderByEmployee = (
    scheduleData: Schedule,
    groups: DataSet<any>,
    items: DataSet<any>,
    problematicShiftIds?: string[],
    selectedEmployees?: Set<string>
) => {
    var newGroups: any[] = []
    let emp_temp = scheduleData.employees
    emp_temp.sort((a, b) => {
        const aKey = a.lastName || a.name;
        const bKey = b.lastName || b.name;
        return aKey.localeCompare(bKey, undefined, { sensitivity: "base" });
    });
    for (const employee of emp_temp) {
        if (selectedEmployees && !selectedEmployees.has(employee.userId)) {
            continue;
        }

        newGroups.push({ id: employee.userId, content: buildEmployeeGroupContent(employee as any) });
        addEmployeeDateBackgrounds(employee, items, employee.unavailableTimeSlots, 'unavailable');
        addEmployeeDateBackgrounds(employee, items, employee.undesiredTimeSlots, 'undesired');
        addEmployeeDateBackgrounds(employee, items, employee.desiredTimeSlots, 'desired');
    }

    addHolidayBackgrounds(scheduleData.holidays, items);

    if (newGroups.length === 0) {
        newGroups.push({ id: 'placeholder', content: createPlaceholderContent('Aucun employé sélectionné') });
    }

    var newItems: any[] = [];
    for (const shift of scheduleData.shifts) {
        if (shift.employee) {
            if (selectedEmployees && !selectedEmployees.has(shift.employee.userId)) {
                continue;
            }

            newItems.push(buildEmployeeShiftItem(shift, problematicShiftIds));
        }
    }
    groups.add(newGroups);
    items.add(newItems);
};

const renderByLocation = (
    scheduleData: Schedule,
    groups: DataSet<any>,
    items: DataSet<any>,
    problematicShiftIds?: string[],
    selectedRoles?: Set<string>
) => {
    const groupsSet = new Map<string, { id: string; content: string }>();

    for (const shift of scheduleData.shifts) {
        const role = shift.roleName || shift.requiredSkill || 'Autre';
        if (!groupsSet.has(role)) groupsSet.set(role, { id: role, content: role });
    }

    for (const employee of scheduleData.employees) {
        const roles = extractRoleNames(employee);
        for (const role of roles) {
            if (!groupsSet.has(role)) groupsSet.set(role, { id: role, content: role });
        }
    }

    const roleOrders = getRoleOrders(scheduleData, true);

    let list = sortRoles(Array.from(groupsSet.values()), roleOrders, (g) => g.content, (g) => g.content);

    if (selectedRoles) {
        list = list.filter(g => selectedRoles.has(g.id));
    }

    for (let l of list) {
        const truncated = l.content.length > 30 ? l.content.slice(0, 30) + "..." : l.content;
        l.content = `<span class="cursor-pointer hover:underline">${escapeHtml(truncated)}</span>`;
    }

    if (list.length === 0) {
        list.push({ id: 'placeholder', content: createPlaceholderContent('Aucun rôle sélectionné') });
    }
    // for (const g of list) {
    //     groups.add(list);
    // }
    groups.add(list);
    addHolidayBackgrounds(scheduleData.holidays, items);
    var newItems: any[] = [];
    for (const shift of scheduleData.shifts) {
        const group = shift.roleName || shift.requiredSkill || 'Autre';

        if (selectedRoles && !selectedRoles.has(group)) {
            continue;
        }

        newItems.push(buildLocationShiftItem(shift, problematicShiftIds));
    }

    items.add(newItems);
};

const computeScheduleWindowStart = (scheduleData: Schedule | null, startDate?: string) => {
    let scheduleStart: string = "";

    if (startDate) {
        scheduleStart = startDate;
    } else {
        if (scheduleData) {
            const dates = scheduleData.shifts.map(shift => new Date(shift.start).toISOString().slice(0, 10));
            const sortedDates = [...dates].sort((a, b) => a.localeCompare(b));
            const first = sortedDates[0];
            scheduleStart = first ?? new Date().toISOString().slice(0, 10);
        }
    }

    return scheduleStart;
};

const renderScheduleToTimeline = (
    scheduleData: Schedule,
    mode: string,
    groups: DataSet<any> | null,
    items: DataSet<any> | null,
    timeline: Timeline | null,
    startDate?: string,
    problematicShiftIds?: string[],
    selectedRoles?: Set<string>,
    selectedEmployees?: Set<string>
) => {
    if (!groups || !items) {
        return;
    }

    groups.clear();
    items.clear();

    if (mode === 'byEmployee') {
        renderByEmployee(scheduleData, groups, items, problematicShiftIds, selectedEmployees);
    } else {
        renderByLocation(scheduleData, groups, items, problematicShiftIds, selectedRoles);
    }

    // if (timeline) {
    //     const scheduleStart = computeScheduleWindowStart(scheduleData, startDate);
    //     const start = new Date(scheduleStart + 'T00:00:00');
    //     const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
    //     timeline.setWindow(start, end, { animation: false });
    // }
};
const setTimelineWindow = (
    scheduleData: Schedule,
    timeline: Timeline | null,
    startDate?: string,
    savedWindow?: { start: Date, end: Date },
) => {
    if (timeline) {
        if (savedWindow) {
            timeline.setWindow(savedWindow.start, savedWindow.end, { animation: false });
        } else {
            const scheduleStart = computeScheduleWindowStart(scheduleData, startDate);
            let windowStart = new Date(scheduleStart + 'T00:00:00');
            let windowEnd = new Date(windowStart.getTime() + INITIAL_WINDOW_DAYS * 24 * 60 * 60 * 1000);

            const hasShiftInWindow = scheduleData.shifts.some((shift) => {
                const shiftStart = new Date(shift.start);
                const shiftEnd = new Date(shift.end);
                return shiftStart < windowEnd && shiftEnd > windowStart;
            });

            if (!hasShiftInWindow) {
                const firstUpcomingShiftStart = scheduleData.shifts
                    .map((shift) => new Date(shift.start))
                    .filter((shiftStart) => !Number.isNaN(shiftStart.getTime()) && shiftStart >= windowStart)
                    .sort((a, b) => +a - +b)[0];

                if (firstUpcomingShiftStart) {
                    windowStart = new Date(firstUpcomingShiftStart);
                    windowStart.setHours(0, 0, 0, 0);
                    windowEnd = new Date(windowStart.getTime() + INITIAL_WINDOW_DAYS * 24 * 60 * 60 * 1000);
                }
            }

            timeline.setWindow(windowStart, windowEnd, { animation: false });
        }
    }
};


type SchedulerTimelineProps = {
    schedule: Schedule;
    mode?: "byEmployee" | "byLocation";
    solving?: boolean;
    startDate?: string | null;
    endDate?: string | null;
    problematicShiftIds?: string[];
    shiftConstraintBreaches?: { [shiftId: string]: string[] };
    appContext: SchedulerAppContext;
    selectedRoles?: Set<string>;
    selectedEmployees?: Set<string>;
    onSelectedRolesChange?: (selected: Set<string>) => void;
    onSelectedEmployeesChange?: (selected: Set<string>) => void;
    selectedCalendarOthers?: Set<string>;
    onSelectedCalendarOthersChange?: (selected: Set<string>) => void;
    onShiftClick?: (shiftId: string) => void;
    onTimelineWindowChange?: (window: { start: Date, end: Date }) => void;
}


function createTimelineData(mode: string, startDate: string, anchor: HTMLElement | null, groupsDS: DataSet<any>, itemsDS: DataSet<any>): TimelineData {
    // groupsDS = new DataSet<any>();
    // const itemsDS = new DataSet<any>();

    const scheduleStart = computeScheduleWindowStart(null, startDate || "");
    const initialStart = new Date(scheduleStart + 'T00:00:00');
    const initialEnd = new Date(initialStart.getTime() + INITIAL_WINDOW_DAYS * 24 * 60 * 60 * 1000);

    const options: TimelineOptions = {
        stack: true,
        timeAxis: { scale: "hour", step: 8 },
        orientation: { axis: "top", item: "top" },
        zoomMin: ZOOM_MIN,
        zoomMax: ZOOM_MAX,
        zoomable: false,
        xss: { disabled: true },
        groupHeightMode: 'fixed',
        height: '100%',
        verticalScroll: true,
        format: TIMELINE_FORMAT,

        start: initialStart,
        end: initialEnd,
    };

    const anchorNode = anchor || document.createElement("div");
    anchorNode.classList.add("TIMELINE");
    anchorNode.style.height = '100%';

    const timeline = new Timeline(anchorNode, itemsDS, groupsDS, options)

    const syncHolidayHeader = () => fillHolidayHeader(anchorNode, timeline);
    timeline.on('rangechange', syncHolidayHeader);
    timeline.on('rangechanged', syncHolidayHeader);

    return {
        itemsDataSet: itemsDS,
        groupsDataSet: groupsDS,
        timeline,
        anchorNode
    }
}

let renderIdProvider = 0;


const SchedulerTimeline = (props: SchedulerTimelineProps) => {

    const schedule = props.schedule;
    const mode = props.mode || "byLocation";
    const startDate = props.startDate;
    const endDate = props.endDate;
    const problematicShiftIds = props.problematicShiftIds;
    const shiftConstraintBreaches = props.shiftConstraintBreaches;

    const containerRef = useRef<HTMLDivElement>(null);
    const hoveredRowRef = useRef<HTMLElement[]>([]);
    const rowBridgeRef = useRef<HTMLDivElement | null>(null);

    // Neither vis panel covers the strip between a label and its row, so the frame needs its own element there.
    const syncRowBridge = useCallback(() => {
        const root = containerRef.current?.querySelector<HTMLElement>(".vis-timeline");
        const center = root?.querySelector<HTMLElement>(".vis-panel.vis-center");
        if (hoveredRowRef.current.some(el => !el.isConnected)) {
            hoveredRowRef.current.forEach(el => el.classList.remove("row-hover-frame"));
            hoveredRowRef.current = [];
        }

        const label = hoveredRowRef.current.find(el => el.matches(".vis-label"));
        const row = hoveredRowRef.current.find(el => el.matches(".vis-group"));

        if (!root || !center || !label || !row) {
            rowBridgeRef.current?.remove();
            return;
        }

        if (!rowBridgeRef.current) {
            rowBridgeRef.current = document.createElement("div");
            rowBridgeRef.current.className = "row-hover-bridge";
            rowBridgeRef.current.appendChild(document.createElement("div"));
        }

        const bridge = rowBridgeRef.current;
        const rootRect = root.getBoundingClientRect();
        const centerRect = center.getBoundingClientRect();
        const labelRect = label.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();

        bridge.style.left = `${labelRect.right - rootRect.left - root.clientLeft}px`;
        bridge.style.width = `${rowRect.left - labelRect.right}px`;
        bridge.style.top = `${centerRect.top - rootRect.top - root.clientTop}px`;
        bridge.style.height = `${centerRect.height}px`;

        const line = bridge.firstElementChild as HTMLElement;
        line.style.top = `${rowRect.top - centerRect.top}px`;
        line.style.height = `${rowRect.height}px`;

        if (bridge.parentNode !== root) {
            root.appendChild(bridge);
        }
    }, []);

    // The vis DOM is cached in appContext and outlives this component.
    useEffect(() => () => {
        hoveredRowRef.current.forEach(el => el.classList.remove("row-hover-frame"));
        rowBridgeRef.current?.remove();
    }, []);
    const timelineRef = useRef<Timeline | null>(null);
    const groupsRef = useRef<DataSet<any> | null>(null);
    const itemsRef = useRef<DataSet<any> | null>(null);
    const propsDataRef = useRef<SchedulerTimelineProps>(null);
    const isInitializedRef = useRef(false);
    const isPinnedRef = useRef(false);
    const justClickedShiftRef = useRef(false);
    const axisModeRef = useRef<"hour" | "day" | null>(null);
    const redrawTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [shiftTooltip, setShiftTooltip] = useState<TooltipState | null>(null);
    const [selectedRole, setSelectedRole] = useState<{ id: string; name: string } | null>(null);
    const [selectedEmployee, setSelectedEmployee] = useState<{ id: string; name: string } | null>(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);

    const selectedRoles = props.selectedRoles || new Set<string>();
    const selectedEmployees = props.selectedEmployees || new Set<string>();
    const onShiftClick = props.onShiftClick || ((id) => {
    })
    // The vis-timeline click handler is not re-registered on every render, so reading these
    // through refs avoids capturing a stale onShiftClick (one where solving was still false).
    const onShiftClickRef = useRef(onShiftClick);
    onShiftClickRef.current = onShiftClick;
    const solvingRef = useRef(props.solving);
    solvingRef.current = props.solving;
    // const setSelectedRoles = props.onSelectedRolesChange || (() => {
    // });
    // const setSelectedEmployees = props.onSelectedEmployeesChange || (() => {
    // });

    const renderId = ++renderIdProvider;
    const debugConsole = {
        log: function (...args: any[]) {
            console.log.apply(console, [renderId, ...args])
        }

    }

    //debugConsole.log("SchedulerTimeline re-render");

    const trySetShiftTooltip = function (val: TooltipState | null) {
        setShiftTooltip(val);
        // debugConsole.log("trySetShiftTooltip");
    }

    useEffect(() => {
        setSelectedRole(null);
        setSelectedEmployee(null);
        setFilterOpen(false);
        setCalendarOpen(false);
    }, [mode]);

    const getTimelineTooltipPosition = useCallback((event: TimelineEvent): { x: number; y: number } | null => {
        if (!containerRef.current) return null;

        const rect = containerRef.current.getBoundingClientRect();
        const sourceEvent = event.event?.srcEvent || event.event;
        const clientX = sourceEvent?.clientX ?? sourceEvent?.pageX ?? 0;
        const clientY = sourceEvent?.clientY ?? sourceEvent?.pageY ?? 0;

        return getTooltipPosition(clientX, clientY, rect);
    }, []);

    const patchShift = (shift: Shift): boolean => {
        const items = props.appContext.locationTimeline?.itemsDataSet;
        if (!items) return false;

        const visible = isShiftVisibleForFilter(shift, mode, selectedRoles, selectedEmployees);
        if (visible) {
            const item = mode === "byEmployee"
                ? buildEmployeeShiftItem(shift, problematicShiftIds)
                : buildLocationShiftItem(shift, problematicShiftIds);
            items.update([item]);
        } else if (items.get(shift.id)) {
            items.remove(shift.id);
        }
        return true;
    };

    useEffect(() => {
        props.appContext.patchShift = patchShift;
    });

    useEffect(() => {
        const prevProps = propsDataRef.current || {} as Partial<SchedulerTimelineProps>;
        let willInit = !propsDataRef.current || !timelineRef.current;
        let willRender = willInit;
        let hasChanges = willInit;
        if (!containerRef.current || isInitializedRef.current) {
            return;
        }

        if (props.appContext.suppressTimelineRebuildRef?.current) {
            props.appContext.suppressTimelineRebuildRef.current = false;
            const onlyScheduleChanged =
                prevProps.mode === mode &&
                prevProps.startDate === startDate &&
                prevProps.problematicShiftIds === problematicShiftIds &&
                prevProps.selectedRoles === props.selectedRoles &&
                prevProps.selectedEmployees === props.selectedEmployees;
            if (onlyScheduleChanged) {
                isInitializedRef.current = true;
                propsDataRef.current = { ...props };
                return () => {
                    isInitializedRef.current = false;
                };
            }
        }

        if (prevProps.mode !== mode) {
            //debugConsole.log("mode changed", prevProps.mode, mode);
            hasChanges = true;
        }
        if (prevProps.schedule !== schedule) {
            //debugConsole.log("schedule changed");
            hasChanges = true;
        }
        if (prevProps.problematicShiftIds !== problematicShiftIds) {
            //debugConsole.log("problematicShiftIds changed");
            hasChanges = true;
        }
        if (prevProps.startDate !== startDate) {
            //debugConsole.log("startDate changed", prevProps.startDate, startDate);
            hasChanges = true;
            willInit = true;
        }
        if (prevProps.selectedRoles !== props.selectedRoles) {
            hasChanges = true;
        }
        if (prevProps.selectedEmployees !== props.selectedEmployees) {
            hasChanges = true;
        }

        if (hasChanges) {
            willInit = true;
            willRender = true;
        }
        if (willRender) {
            //debugConsole.log("Init Timeline");
            //chedule, mode, startDate, problematicShiftIds

            //groupsRef.current =  new DataSet();
            //itemsRef.current = new DataSet();

            // const scheduleStart = computeScheduleWindowStart(schedule, startDate || "");
            // const initialStart = new Date(scheduleStart + 'T00:00:00');
            // const initialEnd = new Date(initialStart.getTime() + INITIAL_WINDOW_DAYS * 24 * 60 * 60 * 1000);


            //@ Init or create timeline instance (Caching)
            let timelineData = props.appContext.locationTimeline || (props.appContext.locationTimeline = createTimelineData(mode, startDate || "", null, new DataSet(), new DataSet()));
            //if(timelineRef.current )timelineRef.current.destroy();
            //let timelineData =  createTimelineData(mode,startDate || "",null,new DataSet(),new DataSet());
            timelineRef.current = timelineData.timeline;
            // // const options: TimelineOptions = {
            //     stack: true,
            //     timeAxis: { scale: "hour", step: 8 },
            //     orientation: { axis: "top" },
            //     zoomMin: ZOOM_MIN,
            //     zoomMax: ZOOM_MAX,
            //     xss: { disabled: true },
            //     format: {
            //         majorLabels: (date: Date) => {
            //             try {
            //                 const formatter = new Intl.DateTimeFormat("fr-FR", {
            //                     weekday: "short",
            //                     day: "2-digit",
            //                     month: "short",
            //                 });
            //                 return formatter.format(date).replaceAll('.', '');
            //             } catch (e) {
            //                 console.error(e);
            //                 return date.toDateString();
            //             }
            //         },
            //     },
            //     start: initialStart,
            //     end: initialEnd,
            // };

            //console.log("mode",mode);

            //@ Append timeline's HTML-node in DOM.
            if (timelineData.anchorNode.parentNode !== containerRef.current && containerRef.current !== timelineData.anchorNode.parentNode) {
                containerRef.current.appendChild(timelineData.anchorNode);
            }


            //console.time("render2");
            //debugConsole.log("useEffect - renderScheduleToTimeline render 2 start",mode, startDate, problematicShiftIds?.length);
            renderScheduleToTimeline(schedule, mode, timelineData.groupsDataSet, timelineData.itemsDataSet, null, startDate || "", problematicShiftIds, selectedRoles, selectedEmployees);
            //  if(timelineRef.current){  timelineRef.current.destroy(); }

            // var timelineData = createTimelineData(mode,startDate || "",containerRef.current,groupsRef.current ,itemsRef.current);
            // timelineRef.current =timelineData.timeline;timelineData.timeline
            //trySetShiftTooltip(null);
            //debugConsole.log("useEffect - renderScheduleToTimeline render 2 end");
            //console.timeEnd("render2")

            //@ Init Timeline factory
            // console.time("Init1");
            // if(timelineRef.current){  timelineRef.current.destroy(); }
            //timelineRef.current = new Timeline(containerRef.current, itemsRef.current, groupsRef.current, options);
            //console.timeEnd("Init1");

            //@ finalise, set window
            if (timelineData.appliedStartDate !== (startDate || "")) {
                setTimelineWindow(schedule, timelineData.timeline, startDate || "", props.appContext.timelineWindow);
                timelineData.appliedStartDate = startDate || "";
            }
            applyAxisForWindow(timelineData.timeline, axisModeRef);

            const finalizeRender = () => {
                const timelineInternals = timelineData.timeline as unknown as { _origRedraw?: () => void };
                if (typeof timelineInternals._origRedraw === 'function') {
                    timelineInternals._origRedraw();
                } else {
                    timelineData.timeline.redraw();
                }
                fillHolidayHeader(timelineData.anchorNode as HTMLElement, timelineData.timeline);
                const scrollPanel = timelineData.anchorNode.querySelector('.vis-panel.vis-vertical-scroll') as HTMLElement | null;
                if (scrollPanel) scrollPanel.scrollTop = 0;
            };

            const ensureTimelinePainted = (attempt: number) => {
                finalizeRender();
                const topPanel = timelineData.anchorNode.querySelector('.vis-panel.vis-top') as HTMLElement | null;
                const visRoot = timelineData.anchorNode.querySelector('.vis-timeline') as HTMLElement | null;
                const stillBroken = (topPanel?.offsetHeight ?? 0) === 0 || visRoot?.style.visibility === 'hidden';
                if (stillBroken && attempt < 60) {
                    redrawTimerRef.current = globalThis.setTimeout(() => ensureTimelinePainted(attempt + 1), 500);
                }
            };

            redrawTimerRef.current = globalThis.setTimeout(() => ensureTimelinePainted(0), 0);

            // containerRef.current.appendChild(timelineData.anchorNode);
        }


        //@ Save last props
        propsDataRef.current = { ...props }
        isInitializedRef.current = !!timelineRef.current;

        return () => {
            isInitializedRef.current = false;
            if (redrawTimerRef.current != null) {
                clearTimeout(redrawTimerRef.current);
                redrawTimerRef.current = null;
            }
        };
    }, [schedule, mode, startDate, problematicShiftIds, selectedRoles, selectedEmployees]);

    // useEffect(() => {
    //     if (!timelineRef.current || !isInitializedRef.current) {
    //         return;
    //     }
    //     debugConsole.log("useEffect - renderScheduleToTimeline render 2 start",mode, startDate, problematicShiftIds);
    //     renderScheduleToTimeline(schedule, mode, groupsRef.current, itemsRef.current, timelineRef.current, startDate, problematicShiftIds);
    //     trySetShiftTooltip(null);

    //     debugConsole.log("useEffect - renderScheduleToTimeline render 2 end");

    // }, [schedule, mode, startDate, problematicShiftIds]);

    useEffect(() => {
        if (!shiftTooltip?.isPinned) {
            return;
        }

        const handleGlobalClick = () => {
            if (justClickedShiftRef.current) {
                justClickedShiftRef.current = false;
                return;
            }
            trySetShiftTooltip(null);
            isPinnedRef.current = false;
        };

        document.addEventListener('click', handleGlobalClick);
        return () => document.removeEventListener('click', handleGlobalClick);
    }, [shiftTooltip?.isPinned]);


    useEffect(() => {
        if (!timelineRef.current || !isInitializedRef.current) {
            return;
        }

        const handleRangeChange = () => {
            const ref = props.appContext.isInteractingRef;
            if (ref) {
                ref.current = true;
            }
            //const timelineWidth = (timelineRef.current as ExtentedTimeline ).dom.root.offsetWidth; // px
        };

        const handleRangeChanged = () => {
            const ref = props.appContext.isInteractingRef;
            if (ref) {
                ref.current = false;
            }
            if (timelineRef.current) {
                applyAxisForWindow(timelineRef.current, axisModeRef);
            }
            if (timelineRef.current && props.onTimelineWindowChange) {
                const window = timelineRef.current.getWindow();
                props.onTimelineWindowChange({ start: window.start, end: window.end });
            }
        };

        const handleChanged = () => {
            const anchor = props.appContext.locationTimeline?.anchorNode as HTMLElement | undefined;
            if (timelineRef.current && anchor) fillHolidayHeader(anchor, timelineRef.current);
            syncRowBridge();
        };
        const handleResize = () => {
            handleRangeChanged();
        };
        timelineRef.current.on('rangechange', handleRangeChange);
        timelineRef.current.on('rangechanged', handleRangeChanged);
        timelineRef.current.on('changed', handleChanged);
        window.addEventListener('resize', handleResize);
        return () => {
            if (timelineRef.current) {
                timelineRef.current.off('rangechange', handleRangeChange);
                timelineRef.current.off('rangechanged', handleRangeChanged);
                timelineRef.current.off('changed', handleChanged);
                window.removeEventListener('resize', handleResize);
            }
        };
    }, [props.appContext]);

    useEffect(() => {
        if (!timelineRef.current || !isInitializedRef.current) {
            return;
        }

        const handleItemOver = (event: TimelineEvent) => {
            if (!event.item || isPinnedRef.current) {
                return;
            }
            if (props.appContext.isInteractingRef?.current) {
                return;
            }

            const shiftId = String(event.item);
            const tooltipData = getShiftTooltipData(shiftId, schedule, shiftConstraintBreaches);
            if (!tooltipData) {
                return;
            }

            const position = getTimelineTooltipPosition(event);
            if (!position) {
                return;
            }

            trySetShiftTooltip({ ...position, ...tooltipData, shiftId, isPinned: false });
        };

        const handleItemOut = () => {
            if (!isPinnedRef.current) {
                trySetShiftTooltip(null);
            }
        };

        const handleClick = (event: TimelineEvent) => {
            if (event.what === "group-label" && event.group && mode === "byLocation") {
                const groupId = String(event.group);
                if (groupId === 'placeholder') {
                    return;
                }

                setSelectedEmployee(null);
                setSelectedRole({ id: groupId, name: groupId });
                return;
            }

            if (event.what === "group-label" && event.group && mode === "byEmployee") {
                const groupId = String(event.group);
                if (groupId === 'placeholder') {
                    return;
                }

                const employee = schedule.employees.find(e => e.userId === groupId);
                if (employee) {
                    setSelectedRole(null);
                    setSelectedEmployee({ id: employee.userId, name: employee.name });
                }
                return;
            }

            if (!event.item) {
                return;
            }

            const shiftId = String(event.item);
            justClickedShiftRef.current = true;

            // Report the click right away while solving: the tooltip guards below can swallow
            // it, and the tooltip itself would render above the modal.
            if (solvingRef.current) {
                trySetShiftTooltip(null);
                isPinnedRef.current = false;
                onShiftClickRef.current(shiftId);
                return;
            }

            if (shiftTooltip?.shiftId === shiftId && isPinnedRef.current) {
                trySetShiftTooltip(null);
                isPinnedRef.current = false;
                onShiftClickRef.current(shiftId);
                return;
            }

            const tooltipData = getShiftTooltipData(shiftId, schedule, shiftConstraintBreaches);
            if (!tooltipData) {
                return;
            }

            const position = getTimelineTooltipPosition(event);
            if (!position) {
                return;
            }

            onShiftClickRef.current(shiftId);
            // trySetShiftTooltip({ ...position, ...tooltipData, shiftId, isPinned: true });
            // isPinnedRef.current = true;


        };

        timelineRef.current.on('itemover', handleItemOver);
        timelineRef.current.on('itemout', handleItemOut);
        timelineRef.current.on('click', handleClick);

        return () => {
            if (timelineRef.current) {
                timelineRef.current.off('itemover', handleItemOver);
                timelineRef.current.off('itemout', handleItemOut);
                timelineRef.current.off('click', handleClick);
            }
        };
    }, [schedule, shiftConstraintBreaches, shiftTooltip?.shiftId, getTimelineTooltipPosition, mode]);

    const handleZoomIn = () => {
        if (timelineRef.current) {
            timelineRef.current.zoomIn(0.5);
        }
    };

    const handleZoomOut = () => {
        if (timelineRef.current) {
            timelineRef.current.zoomOut(0.5);
        }
    };

    // Window navigation keeps the current zoom level: only the anchor moves.
    const moveWindow = (computeStart: (currentStart: Date, durationMs: number) => Date) => {
        if (!timelineRef.current) {
            return;
        }

        const { start, end } = timelineRef.current.getWindow();
        const durationMs = +end - +start;
        let nextStart = +computeStart(new Date(+start), durationMs);

        const periodStart = startDate ? +new Date(startDate + "T00:00:00") : null;
        const periodEnd = endDate ? +new Date(endDate + "T23:59:59") : null;

        if (periodEnd !== null && nextStart + durationMs > periodEnd) {
            nextStart = periodEnd - durationMs;
        }

        // Clamping the start last so a window wider than the period shows it from its beginning.
        if (periodStart !== null && nextStart < periodStart) {
            nextStart = periodStart;
        }

        timelineRef.current.setWindow(new Date(nextStart), new Date(nextStart + durationMs), { animation: false });
    };

    const handleWeekPrev = () => moveWindow((start) => new Date(+start - WEEK_MS));

    const handleWeekNext = () => moveWindow((start) => new Date(+start + WEEK_MS));

    const handleGoToPeriodStart = () => {
        if (!startDate) {
            return;
        }

        moveWindow(() => new Date(startDate + "T00:00:00"));
    };

    const handleGoToPeriodEnd = () => {
        if (!endDate) {
            return;
        }

        moveWindow((_start, durationMs) => new Date(+new Date(endDate + "T23:59:59") - durationMs));
    };

    const getEmployeeItems = () => {
        const employees = [...schedule.employees].sort((a, b) => {
            const aKey = a.lastName || a.name;
            const bKey = b.lastName || b.name;
            return aKey.localeCompare(bKey, undefined, { sensitivity: "base" });
        });

        return employees.map(emp => ({
            id: emp.userId,
            label: emp.name
        }));
    };

    const getRoleItems = () => {
        const rolesSet = new Map<string, string>();

        for (const shift of schedule.shifts) {
            const role = shift.roleName || shift.requiredSkill || 'Autre';
            if (!rolesSet.has(role)) {
                rolesSet.set(role, role);
            }
        }

        for (const employee of schedule.employees) {
            const roles = extractRoleNames(employee);
            for (const role of roles) {
                if (!rolesSet.has(role)) {
                    rolesSet.set(role, role);
                }
            }
        }

        const roleOrders = getRoleOrders(schedule, true);
        const rolesList = Array.from(rolesSet.entries()).map(([id, name]) => ({ id, label: name }));

        return sortRoles(rolesList, roleOrders, (r) => r.label, (r) => r.label);
    };

    const getFilterItems = () => {
        if (mode === 'byEmployee') {
            return getEmployeeItems();
        }

        return getRoleItems();
    };

    const getFilterTitle = () => {
        return mode === 'byEmployee' ? 'Sélection des employés' : 'Sélection des rôles';
    };

    const handleFilterSelectionChange = (selected: Set<string>) => {
        if (mode === 'byEmployee') {
            props.onSelectedEmployeesChange?.(selected);
        } else {
            props.onSelectedRolesChange?.(selected);
        }
    };

    const getActiveFilterCount = () => {
        const activeSet = mode === 'byEmployee' ? selectedEmployees : selectedRoles;
        const totalCount = getFilterItems().length;
        return activeSet.size === totalCount ? 0 : activeSet.size;
    };

    const navActiveSet = mode === 'byEmployee' ? selectedEmployees : selectedRoles;
    const navList = getFilterItems().filter(it => navActiveSet.has(it.id));
    const navCurrentId = mode === 'byEmployee' ? selectedEmployee?.id : selectedRole?.id;
    const navIdx = navList.findIndex(it => it.id === navCurrentId);
    const hasPrev = navIdx > 0;
    const hasNext = navIdx >= 0 && navIdx < navList.length - 1;

    const selectByItem = (item: { id: string; label: string }) => {
        if (mode === 'byEmployee') {
            setSelectedEmployee({ id: item.id, name: item.label });
        } else {
            setSelectedRole({ id: item.id, name: item.label });
        }
    };

    const handleNavPrev = () => {
        if (hasPrev) {
            selectByItem(navList[navIdx - 1]);
        }
    };

    const handleNavNext = () => {
        if (hasNext) {
            selectByItem(navList[navIdx + 1]);
        }
    };

    const clearRowHover = () => {
        hoveredRowRef.current.forEach(el => el.classList.remove("row-hover-frame"));
        hoveredRowRef.current = [];
        syncRowBridge();
    };

    const handleRowHover = (e: React.MouseEvent) => {
        const labelSelector = ".vis-labelset .vis-label";
        const rowSelector = ".vis-foreground .vis-group";
        const hovered = (e.target as HTMLElement).closest<HTMLElement>(`${labelSelector}, ${rowSelector}`);

        if (hovered ? hoveredRowRef.current.includes(hovered) : hoveredRowRef.current.length === 0) {
            return;
        }

        const bridgeRect = rowBridgeRef.current?.firstElementChild?.getBoundingClientRect();
        if (!hovered && bridgeRect && rowBridgeRef.current?.isConnected
            && e.clientX >= bridgeRect.left && e.clientX <= bridgeRect.right
            && e.clientY >= bridgeRect.top && e.clientY <= bridgeRect.bottom) {
            return;
        }

        clearRowHover();

        if (!hovered) {
            return;
        }

        // vis links a row to its label by position only.
        const { top, height } = hovered.getBoundingClientRect();
        const counterpart = Array.from(
            e.currentTarget.querySelectorAll<HTMLElement>(hovered.matches(labelSelector) ? rowSelector : labelSelector)
        ).find(el => {
            const rect = el.getBoundingClientRect();
            return Math.abs(rect.top - top) < 1 && Math.abs(rect.height - height) < 1;
        });

        hoveredRowRef.current = counterpart ? [hovered, counterpart] : [hovered];
        hoveredRowRef.current.forEach(el => el.classList.add("row-hover-frame"));
        syncRowBridge();
    };

    return (
        <div ref={containerRef} className="w-full h-full relative" onMouseMove={handleRowHover} onMouseLeave={clearRowHover}>
            <div
                className="absolute left-2 z-10 grid grid-cols-4 bg-white border border-gray-300 rounded shadow-sm">
                <button
                    onClick={handleZoomIn}
                    className="p-1 flex justify-center hover:bg-gray-100 border-r border-b border-gray-200 cursor-pointer"
                    title="Zoom In"
                >
                    <Plus size={20}/>
                </button>

                <button
                    onClick={handleZoomOut}
                    className="p-1 flex justify-center hover:bg-gray-100 border-r border-b border-gray-200 cursor-pointer"
                    title="Zoom Out"
                >
                    <Minus size={20}/>
                </button>

                <TimelineFilterOverlay
                    isOpen={filterOpen}
                    onOpenChange={setFilterOpen}
                    items={getFilterItems()}
                    selectedItems={mode === 'byEmployee' ? selectedEmployees : selectedRoles}
                    onSelectionChange={handleFilterSelectionChange}
                    title={getFilterTitle()}
                >
                    <FilterButton
                        active={filterOpen}
                        count={getActiveFilterCount()}
                        className="justify-center border-r border-b border-gray-200"
                    />
                </TimelineFilterOverlay>

                <button
                    onClick={() => setCalendarOpen(true)}
                    className="p-1 flex justify-center hover:bg-gray-100 border-b border-gray-200 cursor-pointer"
                    title="Vue calendrier"
                >
                    <CalendarDays size={20}/>
                </button>

                <button
                    onClick={handleGoToPeriodStart}
                    disabled={!startDate}
                    className="p-1 flex justify-center hover:bg-gray-100 border-r border-gray-200 cursor-pointer disabled:opacity-40 disabled:cursor-default"
                    title="Aller au début de la période"
                    aria-label="Aller au début de la période"
                >
                    <ChevronsLeft size={20}/>
                </button>

                <button
                    onClick={handleWeekPrev}
                    className="p-1 flex justify-center hover:bg-gray-100 border-r border-gray-200 cursor-pointer"
                    title="Semaine précédente"
                    aria-label="Semaine précédente"
                >
                    <ChevronLeft size={20}/>
                </button>

                <button
                    onClick={handleWeekNext}
                    className="p-1 flex justify-center hover:bg-gray-100 border-r border-gray-200 cursor-pointer"
                    title="Semaine suivante"
                    aria-label="Semaine suivante"
                >
                    <ChevronRight size={20}/>
                </button>

                <button
                    onClick={handleGoToPeriodEnd}
                    disabled={!endDate}
                    className="p-1 flex justify-center hover:bg-gray-100 cursor-pointer disabled:opacity-40 disabled:cursor-default"
                    title="Aller à la fin de la période"
                    aria-label="Aller à la fin de la période"
                >
                    <ChevronsRight size={20}/>
                </button>
            </div>

            {shiftTooltip && (
                <ShiftBreachTooltip
                    x={shiftTooltip.x}
                    y={shiftTooltip.y}
                    lines={shiftTooltip.lines}
                    shiftId={shiftTooltip.shiftId}
                    containerRef={containerRef}
                    timeRange={shiftTooltip.timeRange}
                    employeeName={shiftTooltip.employeeName}
                    roleName={shiftTooltip.roleName}
                />
            )}

            {selectedRole && (
                <RoleCalendarModal
                    open={!!selectedRole}
                    onClose={() => setSelectedRole(null)}
                    roleName={selectedRole.name}
                    schedule={schedule}
                    problematicShiftIds={problematicShiftIds}
                    shiftConstraintBreaches={shiftConstraintBreaches}
                    periodStartDate={startDate || undefined}
                    periodEndDate={endDate || undefined}
                    onShiftClick={onShiftClick}
                    onPrev={handleNavPrev}
                    onNext={handleNavNext}
                    hasPrev={hasPrev}
                    hasNext={hasNext}
                />
            )}

            {selectedEmployee && (
                <EmployeeCalendarModal
                    open={!!selectedEmployee}
                    onClose={() => setSelectedEmployee(null)}
                    employeeId={selectedEmployee.id}
                    employeeName={selectedEmployee.name}
                    schedule={schedule}
                    problematicShiftIds={problematicShiftIds}
                    shiftConstraintBreaches={shiftConstraintBreaches}
                    periodStartDate={startDate || undefined}
                    periodEndDate={endDate || undefined}
                    onShiftClick={onShiftClick}
                    onPrev={handleNavPrev}
                    onNext={handleNavNext}
                    hasPrev={hasPrev}
                    hasNext={hasNext}
                />
            )}

            {calendarOpen && (
                <GlobalCalendarModal
                    open={calendarOpen}
                    onClose={() => setCalendarOpen(false)}
                    schedule={schedule}
                    problematicShiftIds={problematicShiftIds}
                    shiftConstraintBreaches={shiftConstraintBreaches}
                    periodStartDate={startDate || undefined}
                    periodEndDate={endDate || undefined}
                    roleItems={getRoleItems()}
                    employeeItems={getEmployeeItems()}
                    selectedRoles={selectedRoles}
                    selectedEmployees={selectedEmployees}
                    onSelectedRolesChange={props.onSelectedRolesChange ?? (() => {})}
                    onSelectedEmployeesChange={props.onSelectedEmployeesChange ?? (() => {})}
                    selectedOthers={props.selectedCalendarOthers ?? DEFAULT_CALENDAR_OTHERS_SET}
                    onSelectedOthersChange={props.onSelectedCalendarOthersChange ?? (() => {})}
                    onShiftClick={onShiftClick}
                />
            )}
        </div>
    );
};

export default SchedulerTimeline;


export { createTimelineData }
